import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getServiceBySlug } from "./servicesData";

// Components
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import QuoteModal from "../../components/QuoteModal/QuoteModal";

interface ServicePageTemplateProps {
  slug?: string;
}

const ServicePageTemplate: React.FC<ServicePageTemplateProps> = ({ slug: propSlug }) => {
  const { slug: paramsSlug } = useParams<{ slug: string }>();
  const activeSlug = propSlug || paramsSlug;
  const service = activeSlug ? getServiceBySlug(activeSlug) : undefined;

  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  // Redirect to 404 if service not found
  if (!service) {
    return <Navigate to="/404" replace />;
  }

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="about-page inner-page">
      <div className="body_wrap o-clip">
        <Header />
        <main>
          <PageTitle pageTitle={service.title} pagesub={service.title} parentPage="Services" parentLink="/services" />

          {/* ========= 1. HERO SECTION ========= */}
          <section
            className="service-hero-section pos-rel"
            style={{
              backgroundImage: service.heroImage
                ? `linear-gradient(135deg, rgba(0, 2, 15, 0.85) 0%, rgba(20, 20, 30, 0.95) 100%), url(${service.heroImage})`
                : "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.95) 100%)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              padding: "100px 0 80px",
            }}
          >
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <span
                    style={{
                      background: "rgba(252, 144, 56, 0.15)",
                      color: "#fc9038",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: "600",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      display: "inline-block",
                      marginBottom: "20px",
                      border: "1px solid rgba(252, 144, 56, 0.3)"
                    }}
                  >
                    AquarLabs Service Excellence
                  </span>
                  <h1 className="service-hero-title" style={{ fontSize: "3.2rem", fontWeight: "700", marginBottom: "20px", color: "#fff", lineHeight: "1.2" }}>
                    {service.title}
                  </h1>
                  <p style={{ fontSize: "1.25rem", color: "rgba(255,255,255,0.85)", lineHeight: "1.8", maxWidth: "750px" }}>
                    {service.heroDescription}
                  </p>

                  <div style={{ marginTop: "35px", display: "flex", gap: "15px", flexWrap: "wrap" }}>
                    <button
                      className="thm-btn agency-btn1"
                      onClick={() => setIsQuoteModalOpen(true)}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="text">Get Started Today</span>
                    </button>
                    <a
                      href="#overview-section"
                      className="thm-btn"
                      style={{
                        background: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        padding: "12px 28px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      Explore Details ↓
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ========= 2. KEY METRICS HIGHLIGHTS ========= */}
          {service.keyMetrics && service.keyMetrics.length > 0 && (
            <section style={{ background: "#050714", padding: "40px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="container">
                <div className="row">
                  {service.keyMetrics.map((metric, idx) => (
                    <div key={idx} className="col-lg-3 col-md-6" style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          background: "rgba(255,255,255,0.02)",
                          border: "1px solid rgba(252, 144, 56, 0.2)",
                          borderRadius: "12px",
                          padding: "24px",
                          textAlign: "center",
                          height: "100%",
                          transition: "transform 0.3s ease"
                        }}
                      >
                        <h3 style={{ fontSize: "2.5rem", fontWeight: "800", color: "#fc9038", margin: 0 }}>
                          {metric.value}
                        </h3>
                        <p style={{ fontSize: "1rem", fontWeight: "600", color: "#fff", margin: "6px 0 4px" }}>
                          {metric.label}
                        </p>
                        {metric.description && (
                          <span style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.5)" }}>
                            {metric.description}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ========= 3. OVERVIEW SECTION ========= */}
          <section id="overview-section" className="service-overview-section" style={{ padding: "90px 0", background: "#00020f" }}>
            <div className="container">
              <div className="row">
                <div className="col-lg-10">
                  <span className="section-label" style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                    Service Overview
                  </span>
                  <h2 className="section-title" style={{ fontSize: "2.6rem", margin: "15px 0 30px", color: "#fff", lineHeight: "1.3" }}>
                    {service.overviewTitle}
                  </h2>
                  {service.overviewDescription.map((paragraph, index) => (
                    <p key={index} style={{ fontSize: "1.15rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)", marginBottom: "20px" }}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ========= 4. CORE ACTIVITIES & CAPABILITIES SECTION ========= */}
          <section className="service-activities-section" style={{ padding: "90px 0", background: "rgba(10,12,25,0.9)" }}>
            <div className="container">
              <div className="row mb-40">
                <div className="col-lg-8">
                  <span className="section-label" style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                    {service.activitiesTitle}
                  </span>
                  <h2 className="section-title" style={{ fontSize: "2.4rem", margin: "15px 0", color: "#fff" }}>
                    {service.activitiesSubtitle}
                  </h2>
                </div>
              </div>

              <div className="row">
                {service.activities.map((activity, index) => (
                  <div key={index} className="col-lg-6 col-md-6" style={{ marginBottom: "30px" }}>
                    <div
                      className="activity-card"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "16px",
                        padding: "35px",
                        height: "100%",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <h3 style={{ fontSize: "1.35rem", color: "#fff", marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ color: "#fc9038", fontSize: "1.5rem" }}>●</span>
                        {activity.title}
                      </h3>
                      {activity.description && (
                        <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.7)", marginBottom: "20px", lineHeight: "1.6" }}>
                          {activity.description}
                        </p>
                      )}
                      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {activity.items.map((item, i) => (
                          <li key={i} style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.75)", padding: "8px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ color: "#fc9038", fontWeight: "bold" }}>→</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ========= 5. TECH STACK BADGES SECTION ========= */}
          {service.techStack && service.techStack.length > 0 && (
            <section style={{ padding: "75px 0", background: "#050714", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              <div className="container">
                <div className="text-center mb-40">
                  <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                    Technology & Ecosystem
                  </span>
                  <h2 style={{ fontSize: "2.2rem", margin: "12px 0", color: "#fff" }}>
                    Technologies & Frameworks We Power
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1.05rem" }}>
                    Modern, enterprise-grade technology stack powering our {service.title} solutions
                  </p>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "14px", maxWidth: "900px", margin: "0 auto" }}>
                  {service.techStack.map((tech, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: "rgba(252, 144, 56, 0.08)",
                        color: "#fff",
                        border: "1px solid rgba(252, 144, 56, 0.3)",
                        padding: "10px 22px",
                        borderRadius: "30px",
                        fontSize: "0.95rem",
                        fontWeight: "500",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <span style={{ color: "#fc9038" }}>✦</span> {tech}
                    </span>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ========= 6. DELIVERY METHODOLOGY (PROCESS STEPS) ========= */}
          {service.processSteps && service.processSteps.length > 0 && (
            <section style={{ padding: "90px 0", background: "#00020f" }}>
              <div className="container">
                <div className="text-center mb-50">
                  <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                    Our Delivery Framework
                  </span>
                  <h2 style={{ fontSize: "2.4rem", margin: "12px 0", color: "#fff" }}>
                    How We Deliver Success
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1.05rem", maxWidth: "650px", margin: "0 auto" }}>
                    Structured, battle-tested methodology ensuring speed, security, and exceptional quality
                  </p>
                </div>

                <div className="row">
                  {service.processSteps.map((step, idx) => (
                    <div key={idx} className="col-lg-4 col-md-6" style={{ marginBottom: "30px" }}>
                      <div
                        style={{
                          background: "rgba(255,255,255,0.02)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "16px",
                          padding: "30px",
                          height: "100%",
                          position: "relative"
                        }}
                      >
                        <div
                          style={{
                            fontSize: "2.5rem",
                            fontWeight: "800",
                            color: "rgba(252, 144, 56, 0.4)",
                            marginBottom: "15px"
                          }}
                        >
                          {step.number}
                        </div>
                        <h3 style={{ fontSize: "1.2rem", color: "#fff", marginBottom: "12px", fontWeight: "600" }}>
                          {step.title}
                        </h3>
                        <p style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.65)", margin: 0, lineHeight: "1.6" }}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ========= 7. INDUSTRY USE CASES SECTION ========= */}
          {service.useCases && service.useCases.length > 0 && (
            <section style={{ padding: "90px 0", background: "rgba(10,12,25,0.9)" }}>
              <div className="container">
                <div className="text-center mb-50">
                  <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                    Proven Results
                  </span>
                  <h2 style={{ fontSize: "2.4rem", margin: "12px 0", color: "#fff" }}>
                    Real-World Industry Use Cases
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1.05rem" }}>
                    See how our {service.title} solutions solve critical business challenges
                  </p>
                </div>

                <div className="row">
                  {service.useCases.map((useCase, idx) => (
                    <div key={idx} className="col-lg-6" style={{ marginBottom: "30px" }}>
                      <div
                        style={{
                          background: "rgba(20, 20, 30, 0.8)",
                          border: "1px solid rgba(252, 144, 56, 0.3)",
                          borderRadius: "16px",
                          padding: "35px",
                          height: "100%"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                          <span
                            style={{
                              background: "rgba(252, 144, 56, 0.15)",
                              color: "#fc9038",
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "600"
                            }}
                          >
                            {useCase.category}
                          </span>
                        </div>
                        <h3 style={{ fontSize: "1.3rem", color: "#fff", marginBottom: "12px", fontWeight: "600" }}>
                          {useCase.title}
                        </h3>
                        <p style={{ fontSize: "0.98rem", color: "rgba(255,255,255,0.7)", marginBottom: "20px", lineHeight: "1.6" }}>
                          {useCase.description}
                        </p>
                        <div
                          style={{
                            background: "rgba(252, 144, 56, 0.08)",
                            borderLeft: "3px solid #fc9038",
                            padding: "12px 16px",
                            borderRadius: "0 8px 8px 0"
                          }}
                        >
                          <strong style={{ color: "#fc9038", fontSize: "0.9rem" }}>Impact: </strong>
                          <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" }}>{useCase.impact}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ========= 8. WHY CHOOSE US SECTION ========= */}
          <section className="service-why-us-section" style={{ padding: "90px 0", background: "#050714" }}>
            <div className="container">
              <div className="text-center mb-50">
                <span className="section-label" style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                  Why Choose Us
                </span>
                <h2 className="section-title" style={{ fontSize: "2.5rem", margin: "15px 0 20px", color: "#fff" }}>
                  {service.whyUsTitle}
                </h2>
                <p style={{ fontSize: "1.05rem", color: "rgba(255,255,255,0.65)", maxWidth: "720px", margin: "0 auto" }}>
                  {service.whyUsDescription}
                </p>
              </div>

              <div className="row">
                {service.whyUsItems.map((item, index) => (
                  <div key={index} className="col-lg-4 col-md-6" style={{ marginBottom: "30px" }}>
                    <div
                      style={{
                        background: "rgba(20, 20, 20, 0.8)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "16px",
                        padding: "30px",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        transition: "all 0.3s ease"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "38px",
                            height: "38px",
                            borderRadius: "8px",
                            background: "rgba(252, 144, 56, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fc9038",
                            fontWeight: "bold"
                          }}
                        >
                          ✓
                        </div>
                        <h3 style={{ fontSize: "1.15rem", color: "#fff", margin: 0, fontWeight: "600" }}>{item.title}</h3>
                      </div>
                      <p style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.65)", margin: 0, lineHeight: "1.6" }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ========= 9. INTERACTIVE FAQ ACCORDION SECTION ========= */}
          {service.faqs && service.faqs.length > 0 && (
            <section style={{ padding: "90px 0", background: "#00020f" }}>
              <div className="container">
                <div className="text-center mb-50">
                  <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px", fontWeight: "600" }}>
                    Got Questions?
                  </span>
                  <h2 style={{ fontSize: "2.4rem", margin: "12px 0", color: "#fff" }}>
                    Frequently Asked Questions
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1.05rem" }}>
                    Clear answers to common questions regarding our {service.title} services
                  </p>
                </div>

                <div style={{ maxWidth: "850px", margin: "0 auto" }}>
                  {service.faqs.map((faq, idx) => {
                    const isOpen = openFaqIndex === idx;
                    return (
                      <div
                        key={idx}
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: isOpen ? "1px solid rgba(252, 144, 56, 0.5)" : "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "12px",
                          marginBottom: "16px",
                          overflow: "hidden",
                          transition: "all 0.3s ease"
                        }}
                      >
                        <button
                          onClick={() => toggleFaq(idx)}
                          style={{
                            width: "100%",
                            padding: "20px 24px",
                            background: "transparent",
                            border: "none",
                            color: "#fff",
                            fontSize: "1.1rem",
                            fontWeight: "600",
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer"
                          }}
                        >
                          <span>{faq.question}</span>
                          <span style={{ color: "#fc9038", fontSize: "1.4rem", marginLeft: "15px" }}>
                            {isOpen ? "−" : "+"}
                          </span>
                        </button>

                        {isOpen && (
                          <div style={{ padding: "0 24px 22px", color: "rgba(255,255,255,0.75)", fontSize: "1rem", lineHeight: "1.7" }}>
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ========= 10. CALL TO ACTION & MODAL ========= */}
          <section
            style={{
              padding: "100px 0",
              background: "linear-gradient(135deg, rgba(252, 144, 56, 0.15) 0%, rgba(0, 2, 15, 0.95) 100%)",
              borderTop: "1px solid rgba(252, 144, 56, 0.2)"
            }}
          >
            <div className="container text-center">
              <h2 style={{ fontSize: "2.8rem", color: "#fff", marginBottom: "20px", fontWeight: "700" }}>
                Ready to Transform Your Business with {service.title}?
              </h2>
              <p style={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.8)", marginBottom: "35px", maxWidth: "700px", margin: "0 auto 35px" }}>
                Speak with our technology specialists to discuss your project requirements and receive a custom proposal.
              </p>
              <button
                className="thm-btn agency-btn1"
                onClick={() => setIsQuoteModalOpen(true)}
                style={{ cursor: "pointer" }}
              >
                <span className="text">Contact Us & Get a Free Quote</span>
              </button>
            </div>
          </section>
        </main>
        <Footer />
        <Scrollbar />
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </div>
  );
};

export default ServicePageTemplate;
